import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronIcon, UnfiledIcon } from '../../../icons';
import { useContextMenu } from '../../../hooks';
import { sidebarTreeIndent } from '../../../utils/treeIndent';
import { useCopiedDoc } from '../../../../../hooks/useCopiedDoc';
import { pasteDocMenuLabel } from '../../../../../utils/docClipboard';
import { pasteCopiedDoc } from '../../../../../utils/pasteDoc';
import DocRow from './DocRow';

/**
 * Inbox of docs that are not in a folder — same set as Atlas "Unfiled Documents".
 * Collapsed by default. This is a place, not a catalog of every doc.
 */
const UnfiledSection = ({
  loading,
  unfiledDocs,
  unfiledExpanded,
  onToggleUnfiled,
  selectedDocId,
  onSelectDoc,
  onOpenInNewTab,
  onDeleteDoc,
  organization,
  dropTargetId,
  onDragOverTarget,
  onDragLeaveTarget,
  onDropOnUnfiled,
  createDoc,
}) => {
  const { contextMenu, openContextMenu, closeContextMenu } = useContextMenu();
  const [pasting, setPasting] = useState(false);
  const copied = useCopiedDoc();

  const handleHeaderContextMenu = (e) => {
    if (!copied?.docId || !createDoc) return;
    openContextMenu(e);
  };

  return (
    <div className="sidebar__section sidebar__docs-unfiled">
      <div
        className={`sidebar__section-header-row ${dropTargetId === 'unfiled' ? 'sidebar__docs-drop-target' : ''}`}
        onDragOver={(e) => onDragOverTarget(e, 'unfiled')}
        onDragLeave={onDragLeaveTarget}
        onDrop={onDropOnUnfiled}
        onContextMenu={handleHeaderContextMenu}
      >
        <button
          type="button"
          className="sidebar__section-header"
          onClick={onToggleUnfiled}
          aria-expanded={unfiledExpanded}
        >
          <span className="sidebar__docs-unfiled-icon" aria-hidden>
            <UnfiledIcon size={20} />
          </span>
          <span className="sidebar__section-title">Unfiled</span>
          <span className="sidebar__section-count">{loading ? '…' : unfiledDocs.length}</span>
          <span className="sidebar__section-chevron">
            <ChevronIcon size={9} expanded={unfiledExpanded} />
          </span>
        </button>
      </div>

      {unfiledExpanded && (
        <div className="sidebar__tree-children">
          {loading && unfiledDocs.length === 0 && (
            <p className="sidebar__docs-stub-text" style={{ paddingLeft: sidebarTreeIndent(1) }}>
              Loading docs…
            </p>
          )}
          {!loading && unfiledDocs.length === 0 && (
            <p className="sidebar__docs-stub-text" style={{ paddingLeft: sidebarTreeIndent(1) }}>
              Nothing loose — new docs land here
            </p>
          )}
          {unfiledDocs.map((doc) => (
            <DocRow
              key={doc.id}
              doc={doc}
              depth={1}
              selected={selectedDocId === doc.id}
              onSelect={onSelectDoc}
              onOpenInNewTab={onOpenInNewTab}
              onDeleteDoc={onDeleteDoc}
              organization={organization}
            />
          ))}
        </div>
      )}

      {contextMenu && copied?.docId &&
        createPortal(
          <div
            className="sidebar__context-menu"
            style={{ left: contextMenu.x, top: contextMenu.y }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="sidebar__context-menu-item"
              disabled={pasting}
              onClick={async () => {
                closeContextMenu();
                setPasting(true);
                try {
                  await pasteCopiedDoc({
                    createDoc,
                    moveDocToFolder: organization?.moveDocToFolder,
                    folderId: null,
                  });
                } finally {
                  setPasting(false);
                }
              }}
            >
              {pasteDocMenuLabel(copied.title)}
            </button>
          </div>,
          document.body
        )}
    </div>
  );
};

export default UnfiledSection;
